import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { getFeedback, getFeedbackStats, markFeedbackRead, markFeedbackHelpful } from '../services/shareService';
import { FeedbackCard } from '../components/sharing';
import { Loading } from '../components/common';

const FeedbackDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [feedbackResponse, statsResponse] = await Promise.all([
        getFeedback({ unreadOnly: filter === 'unread' }),
        getFeedbackStats()
      ]);

      if (feedbackResponse.success) {
        setFeedback(feedbackResponse.data.feedback);
        setTotal(feedbackResponse.data.total);
        setUnreadCount(feedbackResponse.data.unreadCount);
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkHelpful = async (feedbackId, isHelpful) => {
    try {
      await markFeedbackHelpful(feedbackId, isHelpful);
      setFeedback(prev =>
        prev.map(f => f.id === feedbackId ? { ...f, isHelpful } : f)
      );
    } catch (error) {
      console.error('Error marking feedback:', error);
    }
  };

  const handleFeedbackClick = async (feedbackItem) => {
    if (!feedbackItem.isRead) {
      try {
        await markFeedbackRead(feedbackItem.id);
        setFeedback(prev =>
          prev.map(f => f.id === feedbackItem.id ? { ...f, isRead: true } : f)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking feedback as read:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Feedback Dashboard</h1>
              <p className="text-sm text-gray-500">
                {unreadCount > 0 ? `${unreadCount} new feedback` : 'All caught up!'}
              </p>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border-none bg-transparent text-gray-700 focus:outline-none focus:ring-0"
            >
              <option value="all">All Feedback</option>
              <option value="unread">Unread Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <StarIconSolid className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.averageRating || 0}
                  </p>
                  <p className="text-xs text-gray-500">Average Rating</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalFeedback || 0}
                  </p>
                  <p className="text-xs text-gray-500">Total Reviews</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.wouldInterviewPercent || 0}%
                  </p>
                  <p className="text-xs text-gray-500">Would Interview</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback list */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loading size="lg" />
          </div>
        ) : feedback.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Feedback Yet
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Share your resume to collect feedback from friends, mentors, or colleagues.
            </p>
            <button
              onClick={() => navigate('/resumes')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to My Resumes
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {feedback.map((item) => (
              <div
                key={item.id}
                onClick={() => handleFeedbackClick(item)}
                className="cursor-pointer"
              >
                <FeedbackCard
                  feedback={item}
                  onMarkHelpful={handleMarkHelpful}
                  showShareTitle={true}
                />
              </div>
            ))}

            {feedback.length < total && (
              <button
                onClick={() => {
                  // Load more implementation
                }}
                className="w-full py-3 text-primary-600 hover:text-primary-700 font-medium"
              >
                Load More
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDashboardPage;
